import { useRoute } from 'vue-router';

import { decodeFromTiptap } from '@/app/scheduler/utils/tiptap';
import { ACTION_TYPES, INTENT_TYPES } from '@/components/VisionAi/constants';
import { buildRequestContext, usePageFilters } from '@/components/VisionAi/utils/request-context';
import { VISION_AI_SENDER } from '@/config';
import { useIdentityStore } from '@/stores/identity';
import { useNotificationStore } from '@/stores/notification';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

type SendUnifiedUserPromptOptions = {
  /** When true, always starts a fresh chat (e.g. settings agent widget). */
  forceNewConversation?: boolean;
};

/**
 * Sends a freeform typed message into Vision chat.
 *
 * This intentionally mirrors the side-panel textbox behavior rather than
 * `visionAiStore.startConversation()`, which is meant for prompt-button flows.
 */
export function useSendUnifiedUserPrompt() {
  const route = useRoute();
  const identityStore = useIdentityStore();
  const notificationStore = useNotificationStore();
  const visionAiStore = useVisionAiStore();
  const visionAiConversationsStore = useVisionAiConversationsStore();
  const visionAiMessagesStore = useVisionAiMessagesStore();
  const pageFilters = usePageFilters();

  async function sendUnifiedUserPrompt(
    rawPrompt: string,
    options: SendUnifiedUserPromptOptions = {},
  ) {
    const message = decodeFromTiptap(rawPrompt).trim();
    if (!message) return false;

    if (options.forceNewConversation || visionAiConversationsStore.summariesSelected) {
      visionAiStore.startNewChat();
    }

    let conversationId = visionAiConversationsStore.currentConversationId;

    if (!conversationId) {
      const conversation = await visionAiConversationsStore.createConversation();
      conversationId = conversation?.id;
    }

    const currentBrandId = identityStore.currentBrand?.id;
    if (!currentBrandId) return false;

    const tempId = visionAiMessagesStore.addOptimisticUserMessage({
      conversationId: conversationId!,
      message,
      intentType: INTENT_TYPES.UNIFIED,
    });

    try {
      await visionAiMessagesStore.createMessage({
        conversationId: conversationId!,
        sender: VISION_AI_SENDER.USER,
        message,
        intentType: INTENT_TYPES.UNIFIED,
        intentQuery: { currentBrandId },
        actionType: ACTION_TYPES.QUESTION,
        usedPrompt: false,
        requestContext: buildRequestContext(route, currentBrandId, pageFilters.value),
        optimisticId: tempId,
      });
      return true;
    } catch {
      visionAiMessagesStore.removeOptimisticUserMessage(tempId);
      notificationStore.setToast({
        message: 'Failed to send message. Please try again.',
        type: 'error',
      });
      return false;
    }
  }

  return { sendUnifiedUserPrompt };
}
